import { MdClose, MdPortrait } from "react-icons/md";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { FILE_STORAGE_SERVER_URL } from "../../helpers/constants";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { getCountryDataByCode, getShortName } from "../../helpers/utils";
import ContactCards from "src/pages/Sources/ContactCards";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import { useMemo } from "react";
import { EPrivacyVisibility } from "../CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import ShinyCard from "../BankCard/ShinyCard";
import SmartImage from "../SmartImage/SmartImage";
import { ICON_SIZES } from "src/constants/icons";

interface INetworkProfile {
  onClose: () => void;
  profileInfo: any;
  actions: any;
}

const NetworkProfile: React.FC<INetworkProfile> = ({
  onClose,
  profileInfo,
  actions = [],
}) => {
  const { t } = useTranslation(["common", "modals"]);
  const privacySettings = profileInfo?.settings?.privacy || {};
  const isContact = false;

  const seeImage = useMemo(() => {
    return (
      privacySettings?.seePicture === EPrivacyVisibility.EVERYONE ||
      (isContact && privacySettings?.seePicture === EPrivacyVisibility.CONTACTS)
    );
  }, [privacySettings]);

  const seeContact = useMemo(() => {
    return (
      privacySettings?.seeContact === EPrivacyVisibility.EVERYONE ||
      (isContact && privacySettings?.seeContact === EPrivacyVisibility.CONTACTS)
    );
  }, [privacySettings]);

  const seeIntro = useMemo(() => {
    return (
      privacySettings?.seeIntro === EPrivacyVisibility.EVERYONE ||
      (isContact && privacySettings?.seeIntro === EPrivacyVisibility.CONTACTS)
    );
  }, [privacySettings]);

  const isPrivate = useMemo(() => {
    return !seeContact && !seeIntro && !seeImage;
  }, [seeIntro, seeContact, seeImage]);

  return (
    <Modal onEscape={() => onClose()} className="md:!w-[500px]">
      <Modal.Header
        title={`${t("modals:network_profile.title")} ${getShortName(
          profileInfo?.name,
        )}`}
        onClose={() => onClose()}
      />
      <Modal.Body>
        <div className={clsx(["flex", "h-full", "flex-col", "gap-4", "pb-2"])}>
          <div className={clsx(["flex", "flex-row", "gap-3"])}>
            <div
              className={clsx([
                "flex",
                "items-center",
                "justify-center",
                "flex-shrink-0",
                "gap-1",
                "overflow-hidden",
                "shadow",
                "rounded-full",
                "bg-checkers",
                "h-[100px]",
                "w-[100px]",
              ])}
            >
              <ShinyCard>
                {seeImage ? (
                  <>
                    {profileInfo?.picture ? (
                      <SmartImage
                        src={`${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${profileInfo?.picture}`}
                        alt={profileInfo?.name}
                        className={clsx(["!h-full", "!w-full", "object-cover"])}
                      />
                    ) : (
                      <div
                        className={clsx([
                          "h-full",
                          "w-full",
                          "flex",
                          "items-center",
                          "justify-center",
                        ])}
                      >
                        <MdPortrait
                          className={clsx([
                            "dark:text-purple-400 text-red-400",
                            "bg-white",
                            "dark:bg-gray-100",
                          ])}
                          size={ICON_SIZES.XL}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    className={clsx([
                      "h-full",
                      "w-full",
                      "flex",
                      "items-center",
                      "justify-center",
                    ])}
                  >
                    <MdPortrait
                      className={clsx([
                        "dark:text-purple-400 text-red-400",
                        "bg-white",
                        "dark:bg-gray-100",
                        "rounded",
                      ])}
                      size={ICON_SIZES.XL}
                    />
                  </div>
                )}
              </ShinyCard>
            </div>
            <div
              className={clsx([
                "flex",
                "flex-col",
                "gap-0",
                "items-start",
                "justify-center",
                "w-full",
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "flex-row",
                  "text-sm",
                  "md:text-sm",
                  "items-center",
                  "gap-1",
                  "font-medium",
                ])}
              >
                {profileInfo?.name}
              </div>

              {profileInfo?.country && (
                <div
                  className={clsx([
                    "flex",
                    "flex-row",
                    "text-xs",
                    "md:text-sm",
                    "items-center",
                    "gap-1",
                  ])}
                >
                  {getCountryDataByCode({ code: profileInfo?.country })?.name ||
                    t("modals:network_profile.no_country")}
                </div>
              )}
              {isPrivate && (
                <div
                  className={clsx(["flex", "w-full", "text-xs", "md:text-sm"])}
                >
                  {t("modals:network_profile.all_private")}
                </div>
              )}
            </div>
          </div>
          {!isPrivate && (
            <>
              {[seeIntro, seeContact]?.includes(true) ? (
                <>
                  {seeIntro && (
                    <CollapsibleBlock
                      title={t(
                        "modals:network_profile.collapsibles.intro.title",
                      )}
                      blocked
                      open
                    >
                      <div
                        className={clsx([
                          // "overflow-auto",
                          // "max-h-[200px]",
                          "md:text-sm",
                          "text-xs",
                          "text-left",
                          "flex",
                          "w-full",
                          "whitespace-pre-line",
                        ])}
                      >
                        {profileInfo?.intro ||
                          `${getShortName(profileInfo?.name)} ${t(
                            "modals:network_profile.collapsibles.intro.no_intro",
                          )}`}
                      </div>
                    </CollapsibleBlock>
                  )}
                  {seeContact && (
                    <CollapsibleBlock
                      title={t(
                        "modals:network_profile.collapsibles.contact.title",
                      )}
                      blocked
                      open
                    >
                      {profileInfo?.settings?.contact?.length ? (
                        <ContactCards
                          data={profileInfo?.settings?.contact || []}
                          showCopy
                        />
                      ) : (
                        <div className="flex w-full  md:text-sm text-xs">
                          No hay información de contacto disponible.
                        </div>
                      )}
                    </CollapsibleBlock>
                  )}
                </>
              ) : (
                ""
              )}
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          actions?.length ? (
            <ButtonGroup align="left" buttons={actions} gap={1} />
          ) : null
        }
        actions={[
          {
            icon: <MdClose size={ICON_SIZES.SM} />,
            onClick: () => onClose(),
            text: t("common:buttons.close"),
          },
        ]}
      />
    </Modal>
  );
};

export default NetworkProfile;
